import { useQuery } from '@tanstack/react-query';

import {
  listAnnouncementReceipts,
  type Announcement,
  type PendingPerson,
  type ReceiptPerson,
} from '@/api/comms/announcements.api';
import { Modal } from '@/components/ui/Modal';

/**
 * Read / acknowledged / pending breakdown for one announcement (poster
 * surface). Pending is a LIVE audience diff computed server-side —
 * members with no receipt row yet.
 */
export function ReceiptsModal({
  open,
  onClose,
  announcement,
}: {
  open: boolean;
  onClose: () => void;
  announcement: Announcement | null;
}) {
  const receiptsQuery = useQuery({
    queryKey: ['comms', 'announcements', announcement?.id, 'receipts'],
    queryFn: () => listAnnouncementReceipts(announcement?.id ?? ''),
    enabled: open && announcement !== null,
  });

  const data = receiptsQuery.data;

  return (
    <Modal open={open} onClose={onClose} title="Receipts" size="lg">
      {announcement ? (
        <div className="space-y-4">
          <p className="text-[13px] text-kw-muted">
            Who has read{announcement.requiresAck ? ' and acknowledged' : ''}{' '}
            <span className="font-medium text-kw-shark">{announcement.title}</span>.
          </p>

          {receiptsQuery.isLoading ? (
            <p className="text-[13px] text-kw-muted">Loading receipts…</p>
          ) : receiptsQuery.error || !data ? (
            <p className="text-[13px] text-kw-danger-text">Could not load receipts — try again.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {announcement.requiresAck ? (
                <ReceiptColumn title="Acknowledged" count={data.acknowledged.length}>
                  {data.acknowledged.map((p) => (
                    <PersonRow key={personKey(p)} name={p.name} email={p.email} />
                  ))}
                </ReceiptColumn>
              ) : null}
              <ReceiptColumn title="Read" count={data.read.length}>
                {data.read.map((p) => (
                  <PersonRow key={personKey(p)} name={p.name} email={p.email} />
                ))}
              </ReceiptColumn>
              <ReceiptColumn title="Pending" count={data.pending.length}>
                {data.pending.map((p: PendingPerson) => (
                  <PersonRow key={p.membershipId} name={p.name} email={p.email} />
                ))}
              </ReceiptColumn>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

function personKey(p: ReceiptPerson): string {
  return p.membershipId ?? p.email;
}

function ReceiptColumn({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-kw-md border border-kw-border">
      <div className="flex items-center justify-between border-b border-kw-border px-3 py-2">
        <span className="text-[12px] font-semibold uppercase tracking-wide text-kw-muted">{title}</span>
        <span className="text-[12px] font-medium text-kw-shark">{count}</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {count === 0 ? (
          <p className="px-3 py-3 text-[12px] italic text-kw-muted">None</p>
        ) : (
          <ul className="divide-y divide-kw-border">{children}</ul>
        )}
      </div>
    </div>
  );
}

function PersonRow({ name, email }: { name: string; email: string }) {
  return (
    <li className="px-3 py-2">
      <p className="truncate text-[13px] text-kw-shark">{name || 'Former teammate'}</p>
      <p className="truncate text-[11px] text-kw-muted">{email}</p>
    </li>
  );
}
